import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { HourlyProfileResponse } from "../pages/schedule-misalignment-route/schedule-misalignment.types"
import { useWindowQuery } from "./use-window-query"
import type { WindowQuery } from "./use-window-query"

// The E2 day leaf (`/neat/insights/schedule-misalignment/{site_id}/{enduse}:{YYYY-MM-DD}`)
// returns an hourly expected-vs-actual profile (not a Sankey column). It's `Any`
// in insights-api's OpenAPI, so it's absent from the generated typed `client()`
// — use the shared axios `httpClient` (same as use-sankey-drill). The response
// mirrors the BE snake_case `HourlyProfileResponse`; the interceptor allowlist
// keeps `api/v1/neat/insights/schedule-misalignment` un-camelCased.
const getHourlyProfile = async (
  organizationId: number,
  siteId: string,
  enduse: string,
  dayKey: string,
  win: WindowQuery
): Promise<HourlyProfileResponse> => {
  try {
    const res = await httpClient.get<HourlyProfileResponse>(
      `/api/v1/neat/insights/schedule-misalignment/${siteId}/${enduse}:${dayKey}`,
      { params: { organization_id: organizationId, ...win } }
    )
    return res.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Lazily resolves the hourly profile for `(siteId, enduse, dayKey)` — the
 * expected-vs-actual line chart shown when a day card is selected in the Sankey.
 * No fetch until all three are set (a day is picked).
 */
export const useHourlyProfile = (
  siteId: string | null,
  enduse: string | null,
  dayKey: string | null
) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const win = useWindowQuery()
  const enabled =
    siteId != null &&
    enduse != null &&
    dayKey != null &&
    activeOrganizationId != null
  const queryKey = useQueryKey(
    ApiQueryName.ScheduleMisalignment,
    "getOne",
    siteId != null && enduse != null && dayKey != null
      ? `hourly:${siteId}:${enduse}:${dayKey}`
      : null,
    win
  )
  return useQuery<HourlyProfileResponse>({
    queryKey,
    enabled,
    queryFn: () =>
      getHourlyProfile(
        Number(activeOrganizationId),
        siteId as string,
        enduse as string,
        dayKey as string,
        win
      ),
  })
}
